import { useState } from "react";
import { Button, RadioGroup, RadioGroupItem, Textarea } from "@mindstonehq/ui";
import { XMarkIcon } from "@heroicons/react/24/outline";
import copy from "copy-to-clipboard";
import { toast } from "@mindstonehq/ui";
import { CopyIcon } from "~/components/icons";
import { GiftIcon } from "@heroicons/react/24/outline";
import { Trans, useLingui } from "@lingui/react/macro";
import { msg } from "@lingui/core/macro";

interface NPSSurveyProps {
  onSubmitScore: (score: number) => void;
  onSubmitFeedback: (feedback: string) => void;
  onDismiss: () => void;
  referralCode?: string;
}

export default function NPSSurvey({ onSubmitScore, onSubmitFeedback, onDismiss, referralCode }: NPSSurveyProps) {
  const { t } = useLingui();
  const [selectedScore, setSelectedScore] = useState<number | null>(null);
  const [feedback, setFeedback] = useState("");

  // Create array of possible scores from 0 to 10
  const scores = Array.from({ length: 11 }, (_, i) => i);

  // Determine if score is in high range (7-10)
  const isHighScore = selectedScore !== null && selectedScore >= 7;

  // Handle score selection
  const handleScoreSelect = (value: string) => {
    const numScore = parseInt(value, 10);
    if (!isNaN(numScore)) {
      setSelectedScore(numScore);
      onSubmitScore(numScore);
    }
  };

  // Handle feedback submission
  const handleSubmitFeedback = () => {
    onSubmitFeedback(feedback.trim());
  };

  // Handle survey dismissal
  const handleDismiss = () => {
    onDismiss();
  };

  return (
    <div className="fixed bottom-3 left-1/2 -translate-x-1/2 z-50 w-full max-w-[788px] px-4 sm:px-8">
      <div className="bg-indigo-200 dark:bg-indigo-900 p-8 sm:p-12 rounded-2xl shadow-2xl relative">
        <button
          onClick={handleDismiss}
          className="absolute top-4 right-4 text-muted-foreground hover:text-foreground"
          aria-label={t(msg`Dismiss survey`)}
        >
          <XMarkIcon className="h-5 w-5" />
        </button>

        <>
          {selectedScore === null ? (
            <div className="text-center pb-8">
              <h2 className="text-lg sm:text-2xl font-semibold text-foreground mb-2">
                <Trans>How likely are you to recommend Mindstone to a friend or colleague?</Trans>
              </h2>
              <p className="text-sm sm:text-base text-muted-foreground mb-6 sm:mb-8">
                <Trans>0 - Not likely at all to 10 - Extremely likely</Trans>
              </p>

              <RadioGroup
                value={selectedScore !== null ? String(selectedScore) : undefined}
                onValueChange={handleScoreSelect}
                className="flex justify-center"
              >
                <div className="inline-grid grid-cols-5 md:grid-cols-11 gap-2 mx-auto">
                  {scores.map((score) => (
                    <div
                      key={score}
                      className={`flex justify-center ${score === 10 ? "col-span-5 md:col-span-1" : ""}`}
                    >
                      <RadioGroupItem value={String(score)} id={`score-${score}`} className="peer sr-only" />
                      <label
                        htmlFor={`score-${score}`}
                        className="flex h-10 w-10 sm:h-12 sm:w-12 cursor-pointer items-center justify-center rounded-md border border-border bg-background text-base text-muted-foreground sm:text-lg font-normal shadow-xs peer-data-[state=checked]:border-2 peer-data-[state=checked]:border-primary hover:bg-muted"
                      >
                        {score}
                      </label>
                    </div>
                  ))}
                </div>
              </RadioGroup>
            </div>
          ) : (
            <div className="flex flex-col lg:flex-row lg:items-start lg:divide-x lg:divide-border">
              {/* ───────── Left side – feedback ───────── */}
              <div className="flex-1 pb-8 lg:pb-0 lg:pr-6">
                <p className="text-lg font-semibold text-foreground mb-6">
                  {isHighScore
                    ? t(msg`Thanks for your feedback! Is there anything we could do to improve Mindstone for you?`)
                    : t(msg`We appreciate your honesty. What's one thing we could improve to make Mindstone more valuable for you?`)}
                </p>
                <Textarea
                  value={feedback}
                  onChange={(e) => setFeedback(e.target.value)}
                  placeholder={t(msg`Share your thoughts here…`)}
                  className="min-h-24 w-full mb-2"
                />

                <Button onClick={handleSubmitFeedback} variant="secondary" size="lg" className="w-full">
                  <Trans>Send feedback</Trans>
                </Button>
              </div>

              {/* ───────── Right side – discount card ───────── */}
              {isHighScore && referralCode && (
                <div className="lg:w-[260px] lg:pl-6 pt-8 lg:pt-0 flex flex-col gap-4">
                  <p className="text-lg font-semibold text-foreground"><Trans>Here’s a thank you!</Trans></p>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    <Trans>Use this code for 20&nbsp;% off your next Mindstone program, or share it with a friend who’s ready to level up their AI skills.</Trans>
                  </p>

                  <div className="bg-muted p-4 rounded-md flex flex-col gap-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium"><Trans>Your 20 % discount code</Trans></span>
                      <GiftIcon className="h-5 w-5 text-muted-foreground" />
                    </div>

                    <div className="w-full border border-dashed border-border rounded-md px-3 py-1.5 bg-background flex items-center justify-between gap-2">
                      <code className="text-sm font-medium whitespace-nowrap">{referralCode}</code>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => {
                          copy(referralCode);
                          toast.success(t(msg`Discount code copied!`));
                        }}
                      >
                        <CopyIcon className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </>
      </div>
    </div>
  );
}
